import React, { useCallback, useEffect, useState } from 'react';
import { dmClient } from '../../services/dmClient';
import { useAuth } from '../../auth/AuthContext';
import './admin.css';

const ALLOWED_EMAIL = 'hemalbpatel@cerebralmachines.ai';

function isAllowed(user) {
  return (user?.email || '').toLowerCase() === ALLOWED_EMAIL;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status }) {
  const cls =
    status === 'New' ? 'admin-status--pending' :
    status === 'Accepted' ? 'admin-status--active' :
    status === 'Not a Fit' || status === 'Closed' ? 'admin-status--disabled' :
    '';
  return <span className={`admin-status ${cls}`}>{status}</span>;
}

export default function PartnerListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100, order_by: 'created_at', order_dir: 'DESC' };
      if (statusFilter) params.status = statusFilter;
      const resp = await dmClient.get('/partner-registrations', { params });
      setItems(resp.data?.registrations || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { if (isAllowed(user)) load(); }, [load, user]);

  if (!isAllowed(user)) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <p style={{ textAlign: 'center', color: 'var(--dm-muted)', padding: 32 }}>
            You do not have access to this page.
          </p>
        </div>
      </div>
    );
  }

  const toggle = (id) => setExpanded((prev) => (prev === id ? null : id));

  return (
    <div className="admin-page">
      <div className="admin-head">
        <h1>Design Partnership Applications</h1>
        <p>{items.length} registration{items.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="admin-card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid var(--dm-border)', borderRadius: 7, fontSize: 13 }}
        >
          <option value="">All</option>
          <option value="New">New</option>
          <option value="Under Review">Under Review</option>
          <option value="Accepted">Accepted</option>
          <option value="Not a Fit">Not a Fit</option>
          <option value="Closed">Closed</option>
        </select>
        <button className="admin-btn" onClick={load} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      <div className="admin-card" style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Company</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Org Type</th>
              <th>Employees</th>
              <th>Claims/mo</th>
              <th>Denials/mo</th>
              <th>Timeline</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={10} className="admin-empty">No registrations found.</td></tr>
            )}
            {items.map((r) => (
              <React.Fragment key={r.id}>
                <tr
                  onClick={() => toggle(r.id)}
                  style={{ cursor: 'pointer' }}
                  title="Click to expand"
                >
                  <td>{fmtDate(r.created_at)}</td>
                  <td style={{ fontWeight: 600 }}>{r.company_name}</td>
                  <td>{r.first_name} {r.last_name}</td>
                  <td className="admin-mono">{r.work_email}</td>
                  <td>{r.org_type}</td>
                  <td>{r.employee_count}</td>
                  <td>{r.monthly_claim_volume?.toLocaleString() || '—'}</td>
                  <td>{r.monthly_denial_volume?.toLocaleString() || '—'}</td>
                  <td>{r.start_timeline}</td>
                  <td><StatusBadge status={r.status} /></td>
                </tr>
                {expanded === r.id && (
                  <tr>
                    <td colSpan={10} style={{ background: 'var(--dm-bg)', padding: '18px 16px' }}>
                      <DetailPanel reg={r} onUpdate={load} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailPanel({ reg, onUpdate }) {
  const [status, setStatus] = useState(reg.status);
  const [saving, setSaving] = useState(false);

  const saveStatus = async () => {
    setSaving(true);
    try {
      await dmClient.put(`/partner-registrations/${reg.id}`, { status });
      onUpdate();
    } catch (_e) { /* ignore */ }
    finally { setSaving(false); }
  };

  const payers = Array.isArray(reg.payer_mix) ? reg.payer_mix.join(', ') : (reg.payer_mix || '—');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px', fontSize: 14 }}>
      <div>
        <strong>Title/Role:</strong> {reg.job_title}
      </div>
      <div>
        <strong>Phone:</strong> {reg.phone_raw}
      </div>
      <div>
        <strong>State/Region:</strong> {reg.state_region}
      </div>
      <div>
        <strong>EHR/PMS:</strong> {reg.ehr_system}
      </div>
      <div>
        <strong>Payer Mix:</strong> {payers}
      </div>
      <div>
        <strong>IP Address:</strong>{' '}
        <span className="admin-mono">{reg.ip_address || '—'}</span>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <strong>Top Denial Reasons:</strong>
        <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{reg.top_denial_reasons || '—'}</p>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <strong>Current Tools/Process:</strong>
        <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{reg.current_tools || '—'}</p>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <strong>Why interested:</strong>
        <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{reg.interest_reason}</p>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <strong>Ideal outcome:</strong>
        <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{reg.ideal_outcome}</p>
      </div>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
        <label style={{ fontWeight: 600, fontSize: 13 }}>Status:</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--dm-border)', borderRadius: 7, fontSize: 13 }}
        >
          <option value="New">New</option>
          <option value="Under Review">Under Review</option>
          <option value="Accepted">Accepted</option>
          <option value="Not a Fit">Not a Fit</option>
          <option value="Closed">Closed</option>
        </select>
        <button
          className="admin-btn admin-btn--primary"
          onClick={saveStatus}
          disabled={saving || status === reg.status}
        >
          {saving ? 'Saving...' : 'Update'}
        </button>
      </div>
    </div>
  );
}
